import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

import { api } from "@/lib/api";


/*
|--------------------------------------------------------------------------
| App Context
|--------------------------------------------------------------------------
| Global application state for:
| - Authentication
| - Cart
| - Wishlist
| - UI state
|
| IMPORTANT:
| Existing localStorage keys and API endpoints are preserved.
|--------------------------------------------------------------------------
*/

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);


/*
|--------------------------------------------------------------------------
| Local Storage Keys
|--------------------------------------------------------------------------
*/

const CART_KEY = "yw_cart_v1";
const WISH_KEY = "yw_wishlist_v1";


/*
|--------------------------------------------------------------------------
| App Provider
|--------------------------------------------------------------------------
*/

export const AppProvider = ({ children }) => {
  /*
  |--------------------------------------------------------------------------
  | Authentication State
  |--------------------------------------------------------------------------
  */

  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | Cart State
  |--------------------------------------------------------------------------
  | Restore existing cart from localStorage.
  |--------------------------------------------------------------------------
  */

  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(CART_KEY) || "[]");
    } catch {
      return [];
    }
  });


  /*
  |--------------------------------------------------------------------------
  | Wishlist State
  |--------------------------------------------------------------------------
  | Restore existing wishlist from localStorage.
  |--------------------------------------------------------------------------
  */

  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(WISH_KEY) || "[]");
    } catch {
      return [];
    }
  });


  /*
  |--------------------------------------------------------------------------
  | UI State
  |--------------------------------------------------------------------------
  */

  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);


  /*
  |--------------------------------------------------------------------------
  | Persist Cart
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);


  /*
  |--------------------------------------------------------------------------
  | Persist Wishlist
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    localStorage.setItem(WISH_KEY, JSON.stringify(wishlist));
  }, [wishlist]);


  /*
  |--------------------------------------------------------------------------
  | Check Current Authentication
  |--------------------------------------------------------------------------
  |
  | IMPORTANT:
  |
  | 401 is EXPECTED when a visitor is not logged in.
  | We intentionally do NOT console.error() for 401.
  |
  | Other errors such as:
  | - 500
  | - Network Error
  | - Backend unavailable
  |
  | are still logged so real problems are visible.
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;
    let cancelled = false;

    /*
     |--------------------------------------------------------------------------
     | PERFORMANCE:
     | Authentication is global but is not required to render the initial
     | shopping homepage. Move the /auth/me request off the critical render
     | path so it does not compete with homepage/API/image requests.
     |
     | If a JWT already exists locally, check auth as soon as the browser gets
     | a chance to do so. For anonymous visitors, use idle time because a 401
     | response provides no shopping-page content.
     |--------------------------------------------------------------------------
    */

    const checkAuth = () => {
      if (cancelled) return;

      api
        .get("/auth/me")
        .then((response) => {
          if (!mounted || cancelled) return;

          setUser(response.data);
        })
        .catch((err) => {
          if (!mounted || cancelled) return;

          /*
           |--------------------------------------------------------------------------
           | 401 = Normal guest visitor
           |--------------------------------------------------------------------------
          */

          if (err.response?.status === 401) {
            setUser(null);
            return;
          }

          /*
           |--------------------------------------------------------------------------
           | Real authentication/API error
           |--------------------------------------------------------------------------
          */

          setUser(null);
          console.error("Auth check failed:", err);
        })
        .finally(() => {
          if (mounted && !cancelled) {
            setAuthChecked(true);
          }
        });
    };

    const tokenExists =
      typeof window !== "undefined" &&
      Boolean(localStorage.getItem("yw_token"));

    let idleId = null;
    let timerId = null;

    if (tokenExists) {
      /*
       * Logged-in users get an immediate asynchronous auth check.
       * The request still runs after the current render work.
       */
      timerId = window.setTimeout(checkAuth, 0);
    } else if (
      typeof window !== "undefined" &&
      "requestIdleCallback" in window
    ) {
      /*
       * Anonymous visitors do not need /auth/me to render the shop.
       * Give critical homepage work priority first.
       */
      idleId = window.requestIdleCallback(checkAuth, {
        timeout: 1200,
      });
    } else {
      /*
       * Browser fallback for environments without requestIdleCallback.
       */
      timerId = window.setTimeout(checkAuth, 250);
    }

    /*
     |--------------------------------------------------------------------------
     | Cleanup
     |--------------------------------------------------------------------------
    */

    return () => {
      mounted = false;
      cancelled = true;

      if (idleId !== null) {
        window.cancelIdleCallback?.(idleId);
      }

      if (timerId !== null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Login
  |--------------------------------------------------------------------------
  */

  const login = useCallback(async (email, password) => {
    const response = await api.post("/auth/login", {
      email,
      password,
    });

    /*
    |--------------------------------------------------------------------------
    | Store JWT token when returned by backend
    |--------------------------------------------------------------------------
    */

    if (response.data?.token) {
      localStorage.setItem("yw_token", response.data.token);
    }

    setUser(response.data.user);

    return response.data.user;
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Register
  |--------------------------------------------------------------------------
  */

  const register = useCallback(async (name, email, password) => {
    const response = await api.post("/auth/register", {
      name,
      email,
      password,
    });

    setUser(response.data.user);

    return response.data.user;
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Logout
  |--------------------------------------------------------------------------
  */

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      /*
      |--------------------------------------------------------------------------
      | Logout should still complete locally even if the backend request fails.
      |
      | We intentionally don't show an error here because the user is
      | being logged out locally regardless.
      |--------------------------------------------------------------------------
      */
    }

    localStorage.removeItem("yw_token");
    setUser(null);
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Add Product To Cart
  |--------------------------------------------------------------------------
  */

  const addToCart = useCallback((product, qty = 1) => {
    setCart((prev) => {
      const index = prev.findIndex(
        (item) => item.product.id === product.id
      );

      /*
      |--------------------------------------------------------------------------
      | Product already exists in cart
      |--------------------------------------------------------------------------
      */

      if (index >= 0) {
        const next = [...prev];

        next[index] = {
          ...next[index],
          quantity: next[index].quantity + qty,
        };

        return next;
      }

      /*
      |--------------------------------------------------------------------------
      | New product
      |--------------------------------------------------------------------------
      */

      return [
        ...prev,
        {
          product,
          quantity: qty,
        },
      ];
    });

    /*
    |--------------------------------------------------------------------------
    | Open cart drawer after adding product
    |--------------------------------------------------------------------------
    */

    setCartOpen(true);
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Update Cart Quantity
  |--------------------------------------------------------------------------
  */

  const updateQty = useCallback((productId, qty) => {
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId
          ? {
              ...item,
              quantity: Math.max(1, qty),
            }
          : item
      )
    );
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Remove Product From Cart
  |--------------------------------------------------------------------------
  */

  const removeFromCart = useCallback((productId) => {
    setCart((prev) =>
      prev.filter((item) => item.product.id !== productId)
    );
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Clear Cart
  |--------------------------------------------------------------------------
  */

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Toggle Wishlist
  |--------------------------------------------------------------------------
  */

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find(
        (item) => item.id === product.id
      );

      /*
      |--------------------------------------------------------------------------
      | Remove existing wishlist item
      |--------------------------------------------------------------------------
      */

      if (exists) {
        return prev.filter(
          (item) => item.id !== product.id
        );
      }

      /*
      |--------------------------------------------------------------------------
      | Add new wishlist item
      |--------------------------------------------------------------------------
      */

      return [...prev, product];
    });
  }, []);


  /*
  |--------------------------------------------------------------------------
  | Check Wishlist
  |--------------------------------------------------------------------------
  */

  const isWishlisted = useCallback(
    (id) => wishlist.some((product) => product.id === id),
    [wishlist]
  );


  /*
  |--------------------------------------------------------------------------
  | Cart Calculations
  |--------------------------------------------------------------------------
  */

  const cartCount = cart.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  const cartSubtotal = cart.reduce(
    (sum, item) => sum + item.quantity * item.product.price,
    0
  );


  /*
  |--------------------------------------------------------------------------
  | Context Value
  |--------------------------------------------------------------------------
  */

  const value = useMemo(
    () => ({
      /*
      |--------------------------------------------------------------------------
      | Authentication
      |--------------------------------------------------------------------------
      */

      user,
      authChecked,
      login,
      register,
      logout,

      /*
      |--------------------------------------------------------------------------
      | Cart
      |--------------------------------------------------------------------------
      */

      cart,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      cartCount,
      cartSubtotal,

      /*
      |--------------------------------------------------------------------------
      | Wishlist
      |--------------------------------------------------------------------------
      */

      wishlist,
      toggleWishlist,
      isWishlisted,

      /*
      |--------------------------------------------------------------------------
      | UI State
      |--------------------------------------------------------------------------
      */

      cartOpen,
      setCartOpen,
      searchOpen,
      setSearchOpen,
    }),
    [
      user,
      authChecked,
      login,
      register,
      logout,
      cart,
      addToCart,
      updateQty,
      removeFromCart,
      clearCart,
      cartCount,
      cartSubtotal,
      wishlist,
      toggleWishlist,
      isWishlisted,
      cartOpen,
      searchOpen,
    ]
  );


  /*
  |--------------------------------------------------------------------------
  | Provider
  |--------------------------------------------------------------------------
  */

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};